import { getIdToken } from "../auth/cognito";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export interface ModelPerformance {
  model_id: string;
  ticker?: string;
  run_id?: string;
  config_key?: string;
  total_predictions?: number;
  mean_return_per_pred?: number;
  std_return_per_pred?: number;
  rolling_sharpe_20?: number;
  hit_rate?: number;
  cum_return_pct?: number;
  last_updated?: string;
  // Enriched from TradingModels on the backend so the dashboard can show
  // which models are currently trading vs paper-live-but-shadowed vs retired.
  // Absent when the model_id has no TradingModels entry (e.g. BACKFILL-* rows).
  state?: "paper-live" | "live" | "retired" | string | null;
  in_active_ensemble?: boolean;
  // Set true when this TradingModels row was manually promoted from the
  // dashboard's Promote button. Pinned rows survive subsequent auto-promote
  // cycles (the auto-promote lambda skips a ticker that has any pinned row).
  pinned?: boolean;
  // Full TradingModels SK ("{run_id}#{config_key}#{guid}"); needed by the
  // state-update endpoint to address the row exactly.
  composite_sk?: string | null;
  // ISO timestamp the row was first written to TradingModels — used by
  // the TickersAdminPage to render "active since" per ticker.
  added_at?: string | null;
}

export interface PredictionRow {
  symbol: string;
  decision_ts: string;
  model_id: string;
  run_id?: string;
  config_key?: string;
  individual_action?: number;
  ensemble_action?: number;
  current_price?: number;
  next_close?: number;
  pnl_pct?: number;
  std_dev?: number;
  confidence?: number;
  // Persisted by the decision Lambda (post-deploy) so the dashboard can label
  // the row without re-deriving from action magnitude. May be absent on rows
  // written before that deploy; fall back to classifyAction(ensemble_action).
  signal_type?: "BUY" | "SELL_SHORT" | "SELL_REDUCE" | "HOLD";
  no_order_reason?: string;
  // Snapshot of the model's TradingModels.state at write time. Immutable
  // history; not affected by later graduation. Absent on rows written before
  // the state-snapshot deploy or by the backfill script.
  state_at_decision?: "paper-live" | "live" | string;
}

export interface EquityPoint {
  decision_ts: string;
  pnl_pct: number;
  cum_return_pct: number;
}

export interface EnsemblePoint {
  decision_ts: string;
  std_dev: number;
  confidence: number;
  ensemble_action: number;
  num_contributors: number;
  contributors: { model_id: string; individual_action: number }[];
}

export interface Bar {
  t: string;       // ISO timestamp
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface ReplayContributor {
  model_id: string;
  run_id?: string;
  config_key?: string;
  individual_action?: number;
  obs_vector?: number[];
  obs_feature_names?: string[];
}

export interface ReplayResponse {
  symbol: string;
  decision_ts: string;
  decision_id?: string;
  ensemble_action?: number;
  current_price?: number;
  std_dev?: number;
  confidence?: number;
  num_models?: number;
  method?: string;
  next_close?: number;
  next_ts?: string;
  pnl_pct?: number;
  signal_type?: "BUY" | "SELL_SHORT" | "SELL_REDUCE" | "HOLD";
  no_order_reason?: string;
  state_at_decision?: "paper-live" | "live" | string;
  contributors: ReplayContributor[];
  bars: Bar[];
  bar_seconds: number;
}

async function authedFetch(
  path: string,
  init: { method?: string; body?: string } = {},
): Promise<unknown> {
  if (!API_BASE) throw new Error("VITE_API_BASE_URL is not set");
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated");
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (init.body) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API_BASE}${path}`, {
    method: init.method ?? "GET",
    headers,
    body: init.body,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json();
}

export async function fetchLeaderboard(opts: { ticker?: string; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.ticker) params.set("ticker", opts.ticker);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  const data = (await authedFetch(`/leaderboard${qs ? `?${qs}` : ""}`)) as {
    models: ModelPerformance[];
    count: number;
  };
  return data;
}

export async function fetchModelDetail(modelId: string, predictions = 50) {
  const data = (await authedFetch(
    `/models/${encodeURIComponent(modelId)}?predictions=${predictions}`,
  )) as {
    model_id: string;
    performance: ModelPerformance;
    recent_predictions: PredictionRow[];
  };
  return data;
}

export async function fetchEquityCurve(modelId: string, limit = 500) {
  const data = (await authedFetch(
    `/models/${encodeURIComponent(modelId)}/equity-curve?limit=${limit}`,
  )) as { model_id: string; points: EquityPoint[]; count: number };
  return data;
}

export async function fetchSymbolEnsemble(symbol: string, limit = 200) {
  const data = (await authedFetch(
    `/symbols/${encodeURIComponent(symbol)}/ensemble?limit=${limit}`,
  )) as { symbol: string; points: EnsemblePoint[]; count: number };
  return data;
}

export interface AccountDelta {
  available: boolean;
  from_ts?: string;
  from_value?: number;
  delta_abs?: number;
  delta_pct?: number;
}

export interface AccountSummary {
  mode: string;
  current: {
    snapshot_ts: string;
    portfolio_value: number;
    cash: number;
    buying_power: number;
    equity: number;
    holdings_value: number;
    num_positions: number;
    realized_pl?: number;
    unrealized_pl?: number;
    account_type?: string;
    on_margin?: boolean;
  } | null;
  deltas: {
    day?: AccountDelta;
    week?: AccountDelta;
    month?: AccountDelta;
    all_time?: AccountDelta;
  };
  snapshot_count?: number;
  message?: string;
}

export interface SymbolDayResponse {
  symbol: string;
  date: string;
  bars: Bar[];
  predictions: PredictionRow[];
  bar_seconds: number;
}

export async function fetchSymbolDay(symbol: string, date: string) {
  return (await authedFetch(
    `/symbols/${encodeURIComponent(symbol)}/day?date=${encodeURIComponent(date)}`,
  )) as SymbolDayResponse;
}

export interface StartTpeResponse {
  // Runs now funnel through the global concurrency gate: if a slot is free
  // the run starts immediately (started=true, run_id/execution_arn set);
  // otherwise it is queued and starts automatically when a slot frees.
  started?: boolean;
  queued?: boolean;
  run_id?: string;
  execution_arn?: string;
  queue_id?: string;
  position?: number;
  in_flight?: number;
  cap?: number;
  started_by: string;
  resumed?: boolean;
}

export async function startTpe(
  opts: {
    phase1Only?: boolean;
    featureConfigName?: string;
    // Workflow + feature overrides forwarded to _start_tpe_for_config:
    // num_rounds, trials_per_round, phase1_timesteps, phase2_timesteps,
    // phase2_top_n, phase2_replicates (+ any feature flags). Workflow keys
    // are split onto the SFN payload; the rest ride under feature_overrides.
    featureOverrides?: Record<string, number | string | boolean>;
    // Optional ticker subset; when set the search runs only for these symbols.
    restrictTickers?: string[];
  } = {},
): Promise<StartTpeResponse> {
  const body: Record<string, unknown> = {};
  if (opts.phase1Only) body.phase1_only = true;
  if (opts.featureConfigName) body.feature_config_name = opts.featureConfigName;
  if (opts.featureOverrides && Object.keys(opts.featureOverrides).length) {
    body.feature_overrides = opts.featureOverrides;
  }
  if (opts.restrictTickers && opts.restrictTickers.length) {
    body.restrict_tickers = opts.restrictTickers;
  }
  return adminPost<StartTpeResponse>("/admin/start-tpe", body);
}

export async function resumeTpe(resumeRunId: string): Promise<StartTpeResponse> {
  return adminPost<StartTpeResponse>("/admin/start-tpe", { resume_run_id: resumeRunId });
}

export interface ScheduleTpeResponse {
  scheduled: boolean;
  queue_id: string;
  fire_at: string; // ISO-8601 UTC
  phase1_only: boolean;
  restrict_tickers: string[];
  scheduled_by: string;
}

/**
 * Schedule a TPE search for a future time instead of running it now. Same
 * params as startTpe; `fireAt` is an ISO-8601 UTC timestamp. The run is
 * enqueued pending and launches at fireAt through the same concurrency gate.
 */
export async function scheduleTpe(
  opts: {
    fireAt: string; // ISO-8601 UTC
    phase1Only?: boolean;
    featureConfigName?: string;
    featureOverrides?: Record<string, number | string | boolean>;
    restrictTickers?: string[];
  },
): Promise<ScheduleTpeResponse> {
  const body: Record<string, unknown> = { fire_at: opts.fireAt };
  if (opts.phase1Only) body.phase1_only = true;
  if (opts.featureConfigName) body.feature_config_name = opts.featureConfigName;
  if (opts.featureOverrides && Object.keys(opts.featureOverrides).length) {
    body.feature_overrides = opts.featureOverrides;
  }
  if (opts.restrictTickers && opts.restrictTickers.length) {
    body.restrict_tickers = opts.restrictTickers;
  }
  return adminPost<ScheduleTpeResponse>("/admin/schedule-tpe", body);
}

export interface ResumableRun {
  run_id: string;
  tickers: string[];
  feature_config_label?: string;
  phase1_only?: boolean;
  failed_at: string | null;
  failed_execution_arn: string;
  resume_kind: "phase1_round" | "phase2";
  resume_from_round: number;
  num_rounds: number;
}

export interface TpeResumableResponse {
  resumable: boolean;
  runs: ResumableRun[];
}

export async function fetchTpeResumable(): Promise<TpeResumableResponse> {
  return (await authedFetch("/admin/tpe-resumable")) as TpeResumableResponse;
}

export interface TpeInstance {
  instance_id: string;
  instance_type: string;
  state: string;
  market_type: "spot" | "on-demand";
  launch_time: string | null;
  private_ip: string | null;
  az: string | null;
  hourly_rate_usd: number | null;
  uptime_hours: number | null;
  cost_usd: number | null;
  rate_source: string;
  running_tasks: number | null;
  pending_tasks: number | null;
}

export interface TpeAsgActivity {
  activity_id: string;
  description: string;
  cause: string;
  status_code: string;
  start_time: string | null;
  end_time: string | null;
  progress: number;
}

export interface TpeStatusResponse {
  state_machine: {
    name: string;
    arn: string | null;
    running_count: number;
    running_executions: Array<{ name: string; execution_arn: string; status: string; start_date: string | null }>;
    latest_execution: { name: string; execution_arn: string | null; status: string; start_date: string | null; stop_date: string | null } | null;
    current_state: string | null;
    current_child_state: string | null;
    current_execution_arn?: string | null;
  };
  map_run: {
    pending: number; running: number; succeeded: number; failed: number;
    timed_out: number; aborted: number; total: number; finished: number; failed_pct: number;
  } | null;
  asg: {
    name: string | null;
    desired: number; min: number; max: number;
    in_service: number;
    instances: TpeInstance[];
    activity: TpeAsgActivity[];
    cost_summary?: {
      current_instances_cost_usd: number;
      terminated_instances_cost_usd: number;
      terminated_instance_count: number;
      terminated_instance_hours: number;
      total_cost_usd: number;
      current_burn_rate_usd_per_hour: number;
      current_burn_rate_usd_per_day: number;
    };
  };
  ecs: {
    cluster: string | null;
    running: number;
    pending: number;
    stopped_recent: number;
    succeeded_recent: number;
    failed_recent: number;
    child_execution_count?: number;
    completion_window_note?: string;
  };
}

export interface SetAsgDesiredResponse {
  asg: string;
  desired: number;
  min: number;
  max: number;
}

export async function setAsgDesired(desired: number): Promise<SetAsgDesiredResponse> {
  if (!API_BASE) throw new Error("VITE_API_BASE_URL is not set");
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE}/admin/asg-desired`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ desired }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return (await res.json()) as SetAsgDesiredResponse;
}

export interface StopTpeResponse {
  executions_stopped: number;
  asg_desired_set: number | null;
  tasks_stopped: number;
  errors: string[];
}

async function adminPost<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  if (!API_BASE) throw new Error("VITE_API_BASE_URL is not set");
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`API ${res.status}: ${errBody}`);
  }
  return (await res.json()) as T;
}

export async function fetchTpeStatus(executionArn?: string | null): Promise<TpeStatusResponse> {
  const suffix = executionArn ? `?execution_arn=${encodeURIComponent(executionArn)}` : "";
  return (await authedFetch(`/admin/tpe-status${suffix}`)) as TpeStatusResponse;
}

export interface TpeActiveRun {
  execution_arn: string;
  name: string;
  run_id: string | null;
  kind: "tpe" | "phase1_only" | "resume";
  // Saved-config name if the run was started from a config dropdown
  // selection; empty string for inline/default-search-space runs.
  feature_config_label?: string;
  // Ticker scope. `restrict_to_ticker` is set ONLY on targeted Phase 2
  // runs (started from RunCompare → "Start Phase 2 for {ticker}") and
  // carries the single ticker being run. `tickers` is the full list for
  // normal multi-ticker TPE runs. Both come straight from the Step
  // Functions execution input. Older API builds won't return these.
  restrict_to_ticker?: string;
  tickers?: string[];
  start_date: string | null;
  current_state: string | null;
  current_child_state: string | null;
  round_label: string;
  num_rounds: number;
  child_count: number;
  map: {
    pending: number; running: number; succeeded: number; failed: number;
    timed_out: number; aborted: number; total: number; finished: number; failed_pct: number;
  };
  tasks_running: number;
  tasks_pending: number;
  tasks_succeeded: number;
  tasks_failed: number;
  ec2_instances_shared: number;
  hourly_cost_usd: number;
}

export interface ActiveRunsResponse {
  runs: TpeActiveRun[];
  shared: {
    asg_total_instances?: number;
    asg_total_hourly_usd?: number;
    total_running_tasks?: number;
  };
}

export async function fetchActiveRuns(): Promise<ActiveRunsResponse> {
  return (await authedFetch("/admin/active-runs")) as ActiveRunsResponse;
}

export interface AwsCostResponse {
  currency: string;
  as_of: string;
  period_start: string;
  period_end: string;
  week_start: string;
  mtd: number;
  wtd: number;
  previous_day: number;
  current_day: number;
  note?: string;
}

export async function fetchAwsCost(): Promise<AwsCostResponse> {
  return (await authedFetch("/admin/aws-cost")) as AwsCostResponse;
}

export interface TpeHistoryRun {
  execution_arn: string;
  name: string | null;
  status: string;
  start_date: string | null;
  stop_date: string | null;
  duration_seconds: number | null;
  tickers: string[];
  ticker_count: number;
  feature_config_label?: string | null;
  /** True when the run was launched against an explicit ticker subset
   *  (Run P1 for ticker subset…) rather than every enabled TradingTickers
   *  row. Older runs that pre-date the field will be `undefined`. */
  is_subset?: boolean;
  child_execution_count: number;
  batches_succeeded: number;
  batches_failed: number;
  batches_aborted: number;
  batches_total: number;
  instance_hours: number;
  total_cost_usd: number;
  spot_terminations: number;
}

export interface TpeHistoryResponse {
  runs: TpeHistoryRun[];
  representative_spot_rate_usd_per_hour: number | null;
  asg_name: string | null;
  note: string;
}

export async function fetchTpeHistory(): Promise<TpeHistoryResponse> {
  return (await authedFetch("/runs/history")) as TpeHistoryResponse;
}

export interface RunTickerStats {
  count: number;
  min: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  max: number;
}

export interface RunCompareEntry {
  run_id: string;
  config: Record<string, unknown>;
  trial_count: number;
  per_ticker: Record<string, RunTickerStats>;
  /** PM-style per-ticker aggregates: median of each promotion-relevant
   *  metric, plus per-trial (CAGR, MDD, # trades) coords for the
   *  risk/return scatter. Absent on responses from older backend builds. */
  per_ticker_pm?: Record<string, RunTickerPmStats>;
  /** Sum / mean / count of `execution_time_seconds` across this run's
   *  Phase 1 trials. Drives the "Phase 1 training cost" card so the user
   *  can pick the cheapest comparable config to promote. Absent on
   *  responses from older backends. */
  phase1_cost?: Phase1Cost;
}

export interface Phase1Cost {
  trial_count: number;
  total_seconds: number;
  mean_seconds: number;
}

export interface MetricStats {
  count: number;
  median: number;
  p25: number;
  p75: number;
  min: number;
  max: number;
}

export interface RunTickerPmStats {
  metrics: {
    cagr_pct?: MetricStats | null;
    max_drawdown_pct?: MetricStats | null;
    win_rate_trade?: MetricStats | null;
    profit_factor?: MetricStats | null;
    buy_and_hold_return_pct?: MetricStats | null;
    num_trades?: MetricStats | null;
    total_return_pct?: MetricStats | null;
    // IS (in-sample / training-window) variants — present only for
    // Phase 2 trials after the equity-curve persistence deploy.
    is_cagr_pct?: MetricStats | null;
    is_max_drawdown_pct?: MetricStats | null;
    is_total_return_pct?: MetricStats | null;
  };
  trial_points: Array<{
    cagr_pct: number;
    max_drawdown_pct: number;
    num_trades: number;
  }>;
  /** Total trial count before server-side capping of trial_points.
   *  When trial_points.length < trial_points_total, the dashboard shows
   *  a "showing N of M" footnote. Absent on responses from older
   *  backends, in which case the dashboard assumes no truncation. */
  trial_points_total?: number;
}

/** One day's worth of (equity, drawdown, rolling Sharpe) emitted by the
 *  backtest container's `_build_equity_curve_payload`. Rolling Sharpe is
 *  `null` during the warmup window (first 20 trading days). */
export interface EquityCurvePoint {
  date: string;
  equity: number;
  drawdown_pct: number;
  rolling_sharpe: number | null;
}

export interface EquityCurveSection {
  points: EquityCurvePoint[];
  summary: {
    cagr_pct?: number;
    max_drawdown_pct?: number;
    total_return_pct?: number;
    final_value?: number;
  };
}

export interface RunEquityCurveResponse {
  run_id: string;
  ticker: string;
  config_key: string;
  replicate: number;
  phase: number;
  trial_number: number;
  initial_cash: number;
  rolling_window_days: number;
  oos: EquityCurveSection;
  is?: EquityCurveSection;
  selected_trial?: {
    run_id: string;
    ticker: string;
    config_key: string;
    replicate: number;
    phase: number | null;
    trial_number: number | null;
    agent_mean_ret_pct: number | null;
    cagr_pct: number | null;
    max_drawdown_pct: number | null;
  };
}

export async function fetchRunEquityCurve(
  runId: string, ticker: string, opts: { phase?: number } = {},
): Promise<RunEquityCurveResponse> {
  const qs = new URLSearchParams();
  if (opts.phase !== undefined) qs.set("phase", String(opts.phase));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return (await authedFetch(
    `/runs/${encodeURIComponent(runId)}/equity/${encodeURIComponent(ticker)}${suffix}`,
  )) as RunEquityCurveResponse;
}

export interface PaperLivePerTicker {
  median: number;
  min: number;
  max: number;
  count: number;
  run_ids: string[];
}

export interface RunCompareResponse {
  phase: number | null;
  runs: RunCompareEntry[];
  config_diff: Array<{ field: string; values: unknown[] }>;
  /** Per-ticker median agent_mean_ret_pct across the active paper-live
   *  ensemble. Absent (or empty object) when no models are deployed. */
  paper_live_per_ticker?: Record<string, PaperLivePerTicker>;
}

export async function fetchRunCompare(runIds: string[], phase?: number): Promise<RunCompareResponse> {
  const qs = new URLSearchParams({ run_ids: runIds.join(",") });
  if (phase !== undefined) qs.set("phase", String(phase));
  return (await authedFetch(`/runs/compare?${qs.toString()}`)) as RunCompareResponse;
}

export interface RunTrial {
  ticker: string;
  trial_number: number | null;
  tpe_round: number | null;
  phase: number | null;
  replicate: string;
  config_key: string;
  agent_mean_ret_pct: number | null;
  total_return_pct: number | null;
  cagr_pct: number | null;
  max_drawdown_pct: number | null;
  buy_and_hold_return_pct: number | null;
  num_trades: number | null;
  win_rate_trade: number | null;
  gamma: number | null;
  gae_lambda: number | null;
  ent_coef: number | null;
  clip_range: number | null;
  n_steps: number | null;
  batch_size: number | null;
  net_architecture: string;
  use_vwap: boolean;
  use_atr: boolean;
  use_returns: boolean;
  use_volatility: boolean;
  use_market_relative: boolean;
  use_overnight: boolean;
  use_sharpe_reward: boolean;
  use_excess_reward: boolean;
  features: string[];
  reward_mode: string;
  sharpe_window: number | null;
  max_hold_bars: number | null;
}

export interface RunDetailResponse {
  run_id: string;
  config: Record<string, unknown>;
  phase: number | null;
  ticker_filter: string | null;
  per_ticker_n: number | null;
  trial_count_total: number;
  trial_count_returned: number;
  truncated: boolean;
  trials: RunTrial[];
  per_ticker: Record<string, RunTickerStats>;
}

export interface FeatureField {
  key: string;
  label: string;
  section: string;
  type: "bool" | "int" | "float" | "string" | "list";
  default: unknown;
  min?: number;
  max?: number;
  search_range?: unknown[];
  categorical?: boolean;
  log_scale?: boolean;
  description: string;
  guidance?: string;
}

export async function fetchFeatureFields(): Promise<{ fields: FeatureField[] }> {
  return (await authedFetch("/admin/feature-fields")) as { fields: FeatureField[] };
}

export interface TpeFeatureConfig {
  config_name: string;
  description: string;
  created_by?: string;
  updated_at?: string;
  // Stamped by dashboard_api when the TPE state machine reports SUCCEEDED
  // for a run that referenced this config. Empty string if never run.
  last_run_phase1_at?: string;
  last_run_full_at?: string;
  overrides: Record<string, unknown>;
}

export async function listConfigs(): Promise<{ configs: TpeFeatureConfig[] }> {
  return (await authedFetch("/admin/configs")) as { configs: TpeFeatureConfig[] };
}

/** One row of the config promotion-rate leaderboard. Aggregated across
 *  every SUCCEEDED TPE run that has ever used the named config, joined
 *  against TradingModels to count (run_id, ticker) pairs that ever made
 *  it to paper-live (retired rows still count). The `never_promoted`
 *  flag is the actionable signal: this config has been run on ≥3
 *  well-spaced days and produced zero promotions. */
export interface ConfigPromotionStats {
  config_name: string;
  description: string;
  runs_total: number;
  /** YYYY-MM-DD list of SUCCEEDED run start dates (deduped, sorted asc). */
  run_dates: string[];
  /** Greedy count of dates in `run_dates` where each is ≥3 days after the
   *  previous one — the "≥3 distinct days, ≥3 days apart" cadence the
   *  retirement rule keys on. */
  well_spaced_run_count: number;
  /** Σ ticker_count across this config's runs — the (run × ticker) denom. */
  run_ticker_pairs_total: number;
  /** Distinct (run_id, ticker) pairs in TradingModels whose source run
   *  used this config. Retired rows count too. */
  promotions_total: number;
  promotion_rate: number;
  /** ISO timestamp of the most-recent TradingModels.added_at attributed
   *  to this config, or empty string if never promoted. */
  last_promotion_at: string;
  /** Σ total_cost_usd across this config's runs — "$ already spent searching". */
  total_cost_usd: number;
  /** Config exists in TpeFeatureConfigsTable but has 0 SUCCEEDED runs. */
  never_used: boolean;
  /** Config has been run on ≥3 well-spaced days and produced 0 promotions. */
  never_promoted: boolean;
  /** Distinct (run_id, ticker) pairs where ≥1 candidate config_key failed
   *  the promote-time consistency gate (CV>=threshold or too few
   *  replicates). A pair can be in BOTH promotions_total and this when
   *  walk-down rejected rank-0 but promoted rank-1+. */
  rejections_total: number;
  /** Strict-failure subset: pairs where every candidate failed the gate
   *  and nothing was promoted. The "config wasted compute" cases. */
  rejection_only_total: number;
  /** Raw audit-row count — walk-down means one (run,ticker) can produce
   *  several rejection events. Useful for "how often is rank-0 rejected
   *  but rank-1 saves the day" diagnosis. */
  rejection_event_count: number;
  /** rejection_only_total / run_ticker_pairs_total. Headline signal:
   *  "X% of attempts under this config produce no tradeable model". */
  rejection_only_rate: number;
  /** ISO timestamp of the most recent rejection event under this config,
   *  or empty string if none. */
  last_rejection_at: string;
}

export interface ConfigPromotionStatsResponse {
  configs: ConfigPromotionStats[];
  generated_at: string;
  rule_min_runs: number;
  rule_min_gap_days: number;
}

export async function fetchConfigPromotionStats(): Promise<ConfigPromotionStatsResponse> {
  return (await authedFetch("/admin/configs/promotion-stats")) as ConfigPromotionStatsResponse;
}

export async function getConfig(name: string): Promise<TpeFeatureConfig> {
  return (await authedFetch(`/admin/configs/${encodeURIComponent(name)}`)) as TpeFeatureConfig;
}

export async function saveConfig(
  config: { config_name: string; description?: string; overrides: Record<string, unknown> },
): Promise<{ config_name: string; ok: boolean }> {
  return adminPost("/admin/configs", config);
}

export async function generateConfigMeta(
  overrides: Record<string, unknown>,
): Promise<{ config_name: string; description: string }> {
  return adminPost("/admin/configs/generate-meta", { overrides });
}

export async function deleteConfig(name: string): Promise<{ config_name: string; deleted: boolean }> {
  if (!API_BASE) throw new Error("VITE_API_BASE_URL is not set");
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE}/admin/configs/${encodeURIComponent(name)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return (await res.json()) as { config_name: string; deleted: boolean };
}

export async function fetchRunDetail(
  runId: string,
  opts: { phase?: number; ticker?: string; perTickerN?: number } = {},
): Promise<RunDetailResponse> {
  const qs = new URLSearchParams();
  if (opts.phase !== undefined) qs.set("phase", String(opts.phase));
  if (opts.ticker) qs.set("ticker", opts.ticker);
  if (opts.perTickerN !== undefined) qs.set("per_ticker_n", String(opts.perTickerN));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return (await authedFetch(`/runs/${encodeURIComponent(runId)}${suffix}`)) as RunDetailResponse;
}

export async function stopTpe(): Promise<StopTpeResponse> {
  return adminPost<StopTpeResponse>("/admin/stop-tpe");
}

export interface StopRunResponse {
  stopped: boolean;
  execution_arn: string;
}

/** Stop one specific TPE run by its execution ARN. The ASG and pending queue
 *  are untouched; the gate refills the freed slot with the next queued run. */
export async function stopRun(executionArn: string): Promise<StopRunResponse> {
  return adminPost<StopRunResponse>("/admin/stop-run", { execution_arn: executionArn });
}

export async function fetchAccountSummary(mode: "paper" | "live" = "paper") {
  return (await authedFetch(`/account/summary?mode=${mode}`)) as AccountSummary;
}

// ---------- Trading-performance report (per-ticker P&L rollup) ----------

export interface TradingPerformanceEquityPoint {
  decision_ts: string;
  cum_return_pct: number;
}

export interface TradingPerformanceTicker {
  ticker: string;
  decision_count: number;
  first_decision_ts: string;
  last_decision_ts: string;
  cum_return_pct: number;
  mean_pnl_pct: number;
  win_rate: number;
  max_drawdown_pct: number;
  signal_counts: Record<string, number>;
  best_decision: { decision_ts: string; pnl_pct: number } | null;
  worst_decision: { decision_ts: string; pnl_pct: number } | null;
  last_7d_cum_return_pct: number | null;
  equity_curve: TradingPerformanceEquityPoint[];
}

export interface TradingPerformanceResponse {
  days: number;
  generated_at: string;
  totals: {
    ticker_count: number;
    winning_count: number;
    losing_count: number;
    decision_count: number;
    median_cum_return_pct: number;
  };
  tickers: TradingPerformanceTicker[];
}

export async function fetchTradingPerformance(days = 30): Promise<TradingPerformanceResponse> {
  return (await authedFetch(`/reports/trading-performance?days=${days}`)) as TradingPerformanceResponse;
}

// ---------- Live broker positions ----------

export interface LivePosition {
  ticker: string;
  qty: number;
  side: string;
  avg_entry_price: number;
  current_price: number;
  market_value: number;
  cost_basis: number;
  unrealized_pl: number;
  unrealized_plpc: number;
  hold_duration_hours: number | null;
  // False when the ticker isn't in the current paper-live roster — usually
  // means a stale position from a retired model that still has open shares.
  in_trading_roster: boolean;
}

export interface LivePositionsResponse {
  generated_at: string;
  position_count: number;
  total_market_value: number;
  total_unrealized_pl: number;
  positions: LivePosition[];
}

export async function fetchLivePositions(): Promise<LivePositionsResponse> {
  return (await authedFetch("/positions/live")) as LivePositionsResponse;
}

// ---------- Decision-time activity (intraday clustering) ----------

export interface DecisionActivityBucket {
  et_time: string;          // "HH:MM" wall clock in America/New_York
  total: number;
  by_signal: {
    BUY?: number;
    SELL_SHORT?: number;
    SELL_REDUCE?: number;
    HOLD?: number;
  };
}

export interface DecisionActivityResponse {
  days: number;
  generated_at: string;
  ticker_count: number;
  total_decisions: number;
  outside_market_hours: number;
  bucket_minutes: number;
  buckets: DecisionActivityBucket[];
  by_day_of_week: number[]; // [Mon, Tue, Wed, Thu, Fri]
}

export async function fetchDecisionActivity(days = 30): Promise<DecisionActivityResponse> {
  return (await authedFetch(`/reports/decision-activity?days=${days}`)) as DecisionActivityResponse;
}

export async function fetchReplay(symbol: string, decisionTs: string, bars = 78) {
  return (await authedFetch(
    `/replay/${encodeURIComponent(symbol)}/${encodeURIComponent(decisionTs)}?bars=${bars}`,
  )) as ReplayResponse;
}

// ---------- TradingModels admin (promote / state / pin) ----------

export interface PromoteResponse {
  ok: boolean;
  ticker: string;
  composite_sk: string;
  model_id: string;
  model_s3_key: string;
  pinned: boolean;
}

/** Manually promote one trial to paper-live, with `pinned=true` so the
 *  next auto-promote run skips this ticker entirely. */
export async function promoteTradingModel(opts: {
  run_id: string;
  ticker: string;
  config_key: string;
  replicate: number | string;
}): Promise<PromoteResponse> {
  return adminPost<PromoteResponse>("/admin/trading-models/promote", opts);
}

/** Promote the best (config_key, replicate) for one (run, ticker) without
 *  the caller having to know which trial wins. Server-side picks the
 *  Phase 2 winner the same way promote_best_model would. Throws a typed
 *  PromoteNoPhase2Error when the response is 404 with code "no_phase2"
 *  so the dashboard can offer to start a targeted Phase 2. */
export class PromoteNoPhase2Error extends Error {
  constructor(public run_id: string, public ticker: string) {
    super(`no Phase 2 data for ${ticker} in ${run_id}`);
  }
}

export async function promoteBestForTicker(opts: {
  run_id: string;
  ticker: string;
}): Promise<PromoteResponse & {
  resolved?: { config_key: string; replicate: string | number; agent_mean_ret_pct: number | null };
}> {
  try {
    return await adminPost("/admin/trading-models/promote-best-for-ticker", opts);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // adminPost throws with the API body inlined; sniff for the 404
    // structured code so the caller can switch UX.
    if (msg.includes('"code":"no_phase2"') || msg.includes('"code": "no_phase2"')) {
      throw new PromoteNoPhase2Error(opts.run_id, opts.ticker);
    }
    throw e;
  }
}

export interface TargetedPhase2Response {
  ok: boolean;
  /** false → started now (execution_arn populated); true → enqueued behind
   *  the global TPE concurrency cap, will start as soon as a slot frees
   *  (queue_id populated, execution_arn absent). */
  queued: boolean;
  run_id: string;
  ticker: string;
  started_by: string;
  /** Present only when queued=false. */
  execution_arn?: string;
  /** Present only when queued=true. */
  queue_id?: string;
  in_flight?: number;
  cap?: number;
}

/** Kick off a Phase 2 fan-out scoped to a single (run, ticker). The
 *  full Phase 2 selection + promote pipeline runs server-side, but only
 *  for that ticker — typically ~350 jobs and ~30 min on the ASG. If the
 *  global TPE concurrency cap is full, the run is queued and starts
 *  automatically when a slot frees (see `queued` field). */
export async function startTargetedPhase2(opts: {
  run_id: string;
  ticker: string;
}): Promise<TargetedPhase2Response> {
  return adminPost("/admin/run-targeted-phase2", opts);
}

/** Update state and/or pinned flag on an existing TradingModels row.
 *  `sk` is the full composite SK ("{run_id}#{config_key}#{guid}"). */
export async function updateTradingModel(opts: {
  ticker: string;
  sk: string;
  state?: "paper-live" | "live" | "retired";
  pinned?: boolean;
}): Promise<{ ok: boolean; ticker: string; sk: string; state?: string; pinned?: boolean }> {
  return adminPost("/admin/trading-models/update", opts);
}

// ---------- Tickers admin (enabled_for_trading / enabled_for_backtest) ----------

/** Per-ticker trading mode. `full` = trade normally; `sells_only` = let the
 *  decision engine still evaluate but block any BUY orders (and skip the
 *  whole decision pipeline when there's no open position); `disabled` =
 *  filter out of every cycle entirely. */
export type TradingMode = "full" | "sells_only" | "disabled";

export interface TickerAdminRow {
  ticker: string;
  trading_mode: TradingMode;
  // Kept on the row for back-compat with existing consumers; derived from
  // trading_mode on the server side (trading_mode !== "disabled").
  enabled_for_trading: boolean;
  enabled_for_backtest: boolean;
  market_comparisons: string[];
  competitors: string[];
}

export interface TickerAdminResponse {
  tickers: TickerAdminRow[];
  count: number;
}

export async function fetchTickersAdmin(): Promise<TickerAdminResponse> {
  return (await authedFetch("/admin/tickers")) as TickerAdminResponse;
}

export interface TickerInvalidEntry {
  ticker: string;
  ok: false;
  error?: string;
}

/** Thrown when /admin/tickers* returns 422 with FMP validation failures.
 *  Lets the UI render a per-symbol "why it failed" instead of a flat
 *  string blob. */
export class TickerValidationError extends Error {
  invalid: TickerInvalidEntry[];
  constructor(message: string, invalid: TickerInvalidEntry[]) {
    super(message);
    this.name = "TickerValidationError";
    this.invalid = invalid;
  }
}

/** Wrap adminPost so 422 bodies are surfaced as TickerValidationError
 *  with the parsed `invalid` list, while all other failures stay as
 *  plain Error (same shape as adminPost). */
async function adminPostTicker<T>(path: string, body: Record<string, unknown>): Promise<T> {
  try {
    return await adminPost<T>(path, body);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const m = msg.match(/^API (\d+): (.*)$/s);
    if (m && m[1] === "422") {
      try {
        const parsed = JSON.parse(m[2]);
        if (parsed && Array.isArray(parsed.invalid)) {
          throw new TickerValidationError(parsed.error || "Ticker validation failed", parsed.invalid);
        }
      } catch (parseErr) {
        if (parseErr instanceof TickerValidationError) throw parseErr;
        // fall through to generic
      }
    }
    throw e;
  }
}

export interface TickerUpdatePayload {
  trading_mode?: TradingMode;
  enabled_for_trading?: boolean;
  enabled_for_backtest?: boolean;
  market_comparisons?: string[];
  competitors?: string[];
}

export async function updateTicker(
  ticker: string,
  payload: TickerUpdatePayload,
): Promise<{ ok: boolean; ticker: string } & TickerUpdatePayload> {
  return adminPostTicker(`/admin/tickers/${encodeURIComponent(ticker)}`, payload as unknown as Record<string, unknown>);
}

/** Back-compat shim — callers that only want to flip flags should use
 *  this so toggle handling stays terse. */
export async function setTickerFlags(
  ticker: string,
  flags: { trading_mode?: TradingMode; enabled_for_trading?: boolean; enabled_for_backtest?: boolean },
): Promise<{ ok: boolean; ticker: string }> {
  return updateTicker(ticker, flags);
}

export interface CreateTickerPayload {
  ticker: string;
  market_comparisons?: string[];
  competitors?: string[];
  trading_mode?: TradingMode;
  enabled_for_trading?: boolean;
  enabled_for_backtest?: boolean;
}

export async function createTicker(
  payload: CreateTickerPayload,
): Promise<{ ok: boolean; ticker: string; market_comparisons: string[]; competitors: string[]; enabled_for_trading: boolean; enabled_for_backtest: boolean }> {
  return adminPostTicker("/admin/tickers", payload as unknown as Record<string, unknown>);
}

// -------- Monitors / alerts --------

export type AlertSeverity = "critical" | "warning" | "info";

export interface MonitorAlert {
  severity: AlertSeverity;
  category: string;
  ticker: string | null;
  message: string;
  detail: Record<string, unknown>;
  ts: string;
}

export interface MonitorCategory {
  id: string;
  title: string;
  description: string;
  alerts: MonitorAlert[];
}

export interface MonitorsResponse {
  summary: { critical: number; warning: number; info: number };
  categories: MonitorCategory[];
  generated_at: string;
}

export async function fetchMonitors(): Promise<MonitorsResponse> {
  return (await authedFetch("/monitors")) as MonitorsResponse;
}

// ---- Scheduled TPE kickoffs (sequential batch over every saved config) ----

export interface ScheduledKickoffItem {
  position: number;
  config_name: string;
}
export interface ScheduleAllConfigsResponse {
  queue_id: string;
  fire_at: string;
  delay_hours: number;
  phase1_only: boolean;
  scheduled: ScheduledKickoffItem[];
  scheduled_by: string;
}

export async function scheduleAllConfigs(
  delayHours: number,
  phase1Only: boolean,
): Promise<ScheduleAllConfigsResponse> {
  return adminPost<ScheduleAllConfigsResponse>("/admin/schedule-all-configs", {
    delay_hours: delayHours,
    phase1_only: phase1Only,
  });
}

// Immediate parallel batch: launches one TPE execution per saved config
// right now (all concurrent on the shared ASG), distinct from the
// sequential fire-later queue that scheduleAllConfigs sets up.
export interface StartAllConfigsResponse {
  queue_id: string;
  // How many configs were enqueued, and how many the gate started right now
  // (the rest run automatically as in-flight runs finish).
  queued: number;
  started_now: number;
  cap: number;
  configs: { position: number; config_name: string }[];
  errors: { config_name: string; error: string }[];
  phase1_only: boolean;
  started_by: string;
  // Echoed back when the kickoff was scoped to a subset of tickers; absent
  // for normal full-portfolio runs.
  tickers_subset?: string[];
}

/** Launches every saved config now.
 *  @param phase1Only  Skip Phase 2 promotion for screening passes.
 *  @param tickers     Optional ticker subset. When provided, each enqueued
 *                     config runs only on these symbols (validated against
 *                     TradingTickers.enabled_for_backtest); when omitted,
 *                     the run uses every backtest-enabled ticker.
 */
export async function startAllConfigs(
  phase1Only: boolean,
  tickers?: string[],
): Promise<StartAllConfigsResponse> {
  const body: Record<string, unknown> = { phase1_only: phase1Only };
  if (tickers && tickers.length > 0) body.tickers = tickers;
  return adminPost<StartAllConfigsResponse>("/admin/start-all-configs", body);
}

export interface KickoffQueueRow {
  queue_id: string;
  position: number;
  config_name: string;
  status: string;  // pending|running|done|failed|cancelled
  fire_at: string;
  scheduled_at: string;
  scheduled_by: string;
  started_at: string;
  finished_at: string;
  run_id: string;
  exec_arn: string;
  phase: string;
  error_msg: string;
}
export interface KickoffQueueSummary {
  queue_id: string;
  fire_at: string;
  scheduled_by: string;
  total: number;
  status_counts: Record<string, number>;
  items: KickoffQueueRow[];
}
export interface KickoffQueueResponse {
  queues: KickoffQueueSummary[];
}

export async function fetchKickoffQueue(): Promise<KickoffQueueResponse> {
  return (await authedFetch("/admin/kickoff-queue")) as KickoffQueueResponse;
}

export interface CancelKickoffQueueResponse {
  queue_id: string;
  cancelled_positions: number[];
  // TPE executions aborted via StopExecution (already-running configs).
  stopped_executions: string[];
  schedule_deleted: boolean;
  schedule_delete_note?: string | null;
  errors?: string[];
}

export async function cancelKickoffQueue(queueId: string): Promise<CancelKickoffQueueResponse> {
  return (await authedFetch(`/admin/kickoff-queue/${encodeURIComponent(queueId)}`, {
    method: "DELETE",
  })) as CancelKickoffQueueResponse;
}

// --- Alpha leaderboard: per-ticker best exposure-matched (trend-neutral) alpha for a run ---
export interface AlphaLeaderboardRow {
  ticker: string;
  config_key: string;
  em_excess_pct: number;          // exposure-matched excess CAGR vs buy-and-hold (the alpha)
  raw_excess_pct: number | null;  // raw excess vs 100%-long buy-and-hold
  avg_net_exposure: number | null;
  agent_cagr_pct: number | null;
  agent_mdd_pct: number | null;
  avg_num_trades: number | null;
  n_replicates: number;
  n_configs: number;
  benchmark_cagr_pct: number | null;
  benchmark_total_return_pct: number | null;
  benchmark_mdd_pct: number | null;
  promoted: boolean;
  exclude_reason?: string;  // present only on `excluded` rows
}

export interface AlphaLeaderboardResponse {
  run_id: string;
  tickers: AlphaLeaderboardRow[];
  count: number;
  note?: string;
  // Tickers pulled from the ranking by the data-sanity / exposure guards
  // (implausible benchmark CAGR or near-cash exposure) — surfaced, not dropped.
  excluded?: AlphaLeaderboardRow[];
}

export async function fetchAlphaLeaderboard(runId: string): Promise<AlphaLeaderboardResponse> {
  return (await authedFetch(
    `/runs/alpha-leaderboard?run_id=${encodeURIComponent(runId)}`,
  )) as AlphaLeaderboardResponse;
}

// --- Daily trade blotter (decisions page) ---
export interface DailyTrade {
  executed_at: string;
  ticker: string;
  side: string;            // "buy" | "sell"
  qty: number | null;
  fill_price: number | null;
  notional: number | null;       // $ value of the trade
  realized_pl: number | null;    // present on closing (sell) trades
  account_equity_after: number | null;
  reason: string | null;
  order_status: string | null;
}

export interface DailyTradesResponse {
  date: string;
  mode: string;
  trades: DailyTrade[];
  summary: {
    trade_count: number;
    buys: number;
    sells: number;
    notional_total: number;
    realized_pl_total: number;
    start_equity: number | null;
    end_equity: number | null;
    day_pl: number | null;
  };
}

export async function fetchDailyTrades(date: string, mode = "paper"): Promise<DailyTradesResponse> {
  const params = new URLSearchParams({ mode });
  if (date) params.set("date", date);
  return (await authedFetch(`/reports/daily-trades?${params.toString()}`)) as DailyTradesResponse;
}
